import React, { useState, useEffect } from 'react';
import { Button, Card, Typography, Modal, message, Empty, Spin, Input } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import Icon from '../../../shared/components/Icon';
import { MdOutlineViewComfy } from 'react-icons/md';
import { FaRegEdit, FaRegShareSquare } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../../shared/services/api/axiosInstance';

const { Title, Text, Paragraph } = Typography;

const DashboardListContent = () => {
  const { team } = useParams();
  const navigate = useNavigate();
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [dashboardToDelete, setDashboardToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [publicUrl, setPublicUrl] = useState(null);
  const [urlCopied, setUrlCopied] = useState(false);

  // Fetch dashboards
  const fetchDashboards = async () => {
    try {
      setLoading(true);
      // Get team context from localStorage like other pages do
      const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
      const teamId = selectedTeamData._id || localStorage.getItem('selectedTeam');
      
      if (!teamId) {
        console.error('No team selected');
        message.error('Please select a team first');
        setLoading(false);
        return;
      }
      
      const response = await axiosInstance.get(`/dashboards/team/${teamId}`);
      setDashboards(response.data.data || []);
    } catch (error) {
      console.error('Error fetching dashboards:', error);
      message.error('Failed to fetch dashboards');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboards();
  }, []);

  // Handle create new dashboard
  const handleCreateNew = () => {
    navigate(`/${team}/custom-dashboard?new=true`);
  };

  // Handle view dashboard
  const handleViewDashboard = (dashboardId) => {
    navigate(`/${team}/custom-dashboard?id=${dashboardId}`);
  };

  // Handle edit dashboard
  const handleEditDashboard = (dashboardId) => {
    navigate(`/${team}/custom-dashboard?id=${dashboardId}&edit=true`);
  };

  // Handle share click
  const handleShareClick = async (dashboard) => {
    try {
      setSelectedDashboard(dashboard);
      
      // Get the currently selected team ID
      const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
      const teamId = selectedTeamData.id || selectedTeamData._id || localStorage.getItem('selectedTeam');
      
      if (!teamId) {
        message.error('Team ID not found. Please select a team.');
        return;
      }
      
      // Get or create public share URL
      const response = await axiosInstance.get(`/dashboards/${dashboard._id}/share?teamId=${teamId}`);
      if (response.data.success && response.data.data.isPublic) {
        setPublicUrl(response.data.data.publicUrl);
      } else {
        // Create new share URL
        const shareResponse = await axiosInstance.post(`/dashboards/${dashboard._id}/share`, {
          teamId: teamId
        });
        if (shareResponse.data.success) {
          setPublicUrl(shareResponse.data.data.publicUrl);
        }
      }
      setShareModalVisible(true);
    } catch (error) {
      console.error('Error getting share URL:', error);
      message.error('Failed to get share URL');
    }
  };

  // Copy public URL to clipboard
  const copyPublicUrl = async () => {
    if (!publicUrl) return;
    
    try {
      await navigator.clipboard.writeText(publicUrl);
      setUrlCopied(true);
      message.success('Public URL copied to clipboard!');
      setTimeout(() => setUrlCopied(false), 2000);
    } catch (error) {
      console.error('Error copying URL:', error);
      message.error('Failed to copy URL');
    }
  };

  // Revoke public access
  const revokePublicAccess = async () => {
    if (!selectedDashboard) return;
    
    try {
      // Get the currently selected team ID
      const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
      const teamId = selectedTeamData.id || selectedTeamData._id || localStorage.getItem('selectedTeam');
      
      if (!teamId) {
        message.error('Team ID not found. Please select a team.');
        return;
      }
      
      await axiosInstance.delete(`/dashboards/${selectedDashboard._id}/share?teamId=${teamId}`);
      setPublicUrl(null);
      setShareModalVisible(false);
      message.success('Public access revoked');
    } catch (error) {
      console.error('Error revoking access:', error);
      message.error('Failed to revoke public access');
    }
  };

  // Handle delete dashboard
  const handleDeleteClick = (dashboard) => {
    setDashboardToDelete(dashboard);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!dashboardToDelete) return;

    try {
      setDeleting(true);
      // Get team context for delete operation
      const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
      const teamId = selectedTeamData._id || localStorage.getItem('selectedTeam');
      await axiosInstance.delete(`/dashboards/team/${teamId}/${dashboardToDelete._id}`);
      message.success('Dashboard deleted successfully');
      setDeleteModalVisible(false);
      setDashboardToDelete(null);
      fetchDashboards(); // Refresh the list
    } catch (error) {
      console.error('Error deleting dashboard:', error);
      message.error('Failed to delete dashboard');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setDashboardToDelete(null);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Removed status helpers as active tag is no longer displayed

  return (
    <div className="flex-1 px-6 pt-6 overflow-auto min-w-0" style={{ backgroundColor: '#ffffff' }}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <Title level={2} className="mb-2" style={{ 
              color: '#1d0c46',
              fontFamily: 'Raleway, sans-serif',
              fontWeight: 600
            }}>
              Dashboards
            </Title>
            <Text style={{ 
              color: '#666666',
              fontFamily: 'Raleway, sans-serif',
              fontSize: '16px'
            }}>
              Create and manage your personalized dashboards
            </Text>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateNew}
            size="large"
            style={{ 
              backgroundColor: '#1d0c46',
              borderColor: '#1d0c46',
              fontFamily: 'Raleway, sans-serif',
              fontWeight: 500,
              borderRadius: '0px'
            }}
          >
            Create New Dashboard
          </Button>
        </div>

        {/* Dashboards List */}
        <Spin spinning={loading}>
          {dashboards.length === 0 ? (
            <Card className="text-center py-12 glass-effect">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <div>
                    <Title level={4} style={{ 
                      color: '#1d0c46',
                      fontFamily: 'Raleway, sans-serif',
                      fontWeight: 500
                    }}>
                      No dashboards yet
                    </Title>
                    <Paragraph style={{ 
                      color: '#666666',
                      fontFamily: 'Raleway, sans-serif',
                      fontSize: '16px'
                    }}>
                      Create your first custom dashboard to get started
                    </Paragraph>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleCreateNew}
                      size="large"
                      style={{ 
                        backgroundColor: '#1d0c46',
                        borderColor: '#1d0c46',
                        fontFamily: 'Raleway, sans-serif',
                        fontWeight: 500,
                        borderRadius: '0px'
                      }}
                    >
                      Create Dashboard
                    </Button>
                  </div>
                }
              />
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {dashboards.map((dashboard) => (
                <Card
                  key={dashboard._id}
                  hoverable
                  className="dashboard-card glass-effect"
                  style={{
                    borderRadius: '0px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    border: '1px solid #DDE2EE'
                  }}
                  styles={{ body: { padding: '24px' } }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                  }}
                >
                  {/* Header with title */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-3">
                      <Title level={4} className="mb-0" style={{ 
                        color: '#0f172a',
                        fontFamily: 'Raleway, sans-serif',
                        fontWeight: 600,
                        fontSize: '18px',
                        lineHeight: '1.2'
                      }}>
                        {dashboard.name}
                      </Title>
                    </div>
                    
                  </div>

                  {/* Description */}
                  {dashboard.description && (
                    <div className="mb-4">
                      <Text style={{ 
                        color: '#666666',
                        fontFamily: 'Raleway, sans-serif',
                        fontSize: '14px',
                        lineHeight: '1.4',
                        display: 'block'
                      }}>
                        {dashboard.description}
                      </Text>
                    </div>
                  )}

                  {/* Metadata section */}
                  <div className="mb-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon name="calendar" size={16} color="#475569" />
                        <Text style={{ 
                          color: '#475569',
                          fontFamily: 'Raleway, sans-serif',
                          fontSize: '13px'
                        }}>
                          Updated {formatDate(dashboard.updatedAt)}
                        </Text>
                      </div>
                      
                    </div>
                    
                    <div className="flex items-center">
                      <Text style={{ 
                        color: '#666666',
                        fontFamily: 'Raleway, sans-serif',
                        fontSize: '14px'
                      }}>
                        {dashboard.widgets?.length || 0} widgets
                        {dashboard.textWidgets?.length > 0 && ` • ${dashboard.textWidgets.length} text elements`}
                      </Text>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2 pt-2 border-t border-gray-100">
                    <Button
                      type="primary"
                      icon={<MdOutlineViewComfy />}
                      onClick={() => handleViewDashboard(dashboard._id)}
                      size="small"
                      style={{ 
                        backgroundColor: '#1d0c46',
                        borderColor: '#1d0c46',
                        fontFamily: 'Raleway, sans-serif',
                        fontWeight: 500,
                        borderRadius: '0px',
                        height: '32px'
                      }}
                    >
                      View
                    </Button>
                    <Button
                      icon={<FaRegEdit />}
                      onClick={() => handleEditDashboard(dashboard._id)}
                      size="small"
                      style={{ 
                        color: '#1d0c46',
                        borderColor: '#1d0c46',
                        backgroundColor: 'transparent',
                        fontFamily: 'Raleway, sans-serif',
                        fontWeight: 500,
                        borderRadius: '0px',
                        height: '32px'
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      icon={<FaRegShareSquare />}
                      onClick={() => handleShareClick(dashboard)}
                      size="small"
                      style={{ 
                        color: '#1d0c46',
                        borderColor: '#1d0c46',
                        backgroundColor: 'transparent',
                        fontFamily: 'Raleway, sans-serif',
                        fontWeight: 500,
                        borderRadius: '0px',
                        height: '32px'
                      }}
                    >
                      Share
                    </Button>
                    <Button
                      icon={<Icon name="delete" />}
                      onClick={() => handleDeleteClick(dashboard)}
                      size="small"
                      style={{ 
                        color: '#666666',
                        borderColor: '#DDE2EE',
                        backgroundColor: 'transparent',
                        fontFamily: 'Raleway, sans-serif',
                        fontWeight: 500,
                        borderRadius: '0px',
                        height: '32px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#000000';
                        e.currentTarget.style.borderColor = '#000000';
                        e.currentTarget.style.backgroundColor = '#F5F5F5';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#666666';
                        e.currentTarget.style.borderColor = '#DDE2EE';
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Spin>
      </div>

      {/* Share Modal */}
      <Modal
        title="Share Dashboard"
        open={shareModalVisible}
        onCancel={() => {
          setShareModalVisible(false);
          setUrlCopied(false);
        }}
        footer={[
          publicUrl ? (
            <Button
              key="revoke"
              danger
              onClick={revokePublicAccess}
              style={{ 
                fontFamily: 'Raleway, sans-serif',
                fontWeight: 500
              }}
            >
              Revoke Access
            </Button>
          ) : null,
          <Button
            key="close"
            onClick={() => {
              setShareModalVisible(false);
              setUrlCopied(false);
            }}
            style={{ 
              fontFamily: 'Raleway, sans-serif',
              fontWeight: 500
            }}
          >
            Close
          </Button>
        ]}
        style={{ 
          fontFamily: 'Raleway, sans-serif'
        }}
      >
        <div style={{ padding: '16px 0' }}>
          <Text style={{ 
            color: '#666666',
            fontFamily: 'Raleway, sans-serif',
            fontSize: '14px',
            display: 'block',
            marginBottom: '16px'
          }}>
            Anyone with this link can view your dashboard. You can revoke access at any time.
          </Text>
          
          {publicUrl ? (
            <div style={{ 
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              padding: '12px',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px',
              border: '1px solid #DDE2EE'
            }}>
              <Input
                value={publicUrl}
                readOnly
                style={{
                  fontFamily: 'Raleway, sans-serif',
                  fontSize: '13px',
                  flex: 1
                }}
              />
              <Button
                onClick={copyPublicUrl}
                style={{
                  fontFamily: 'Raleway, sans-serif',
                  fontWeight: 500,
                  borderColor: '#1d0c46',
                  color: '#1d0c46'
                }}
              >
                {urlCopied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '24px' }}>
              <Spin size="large" />
            </div>
          )}
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title={
          <span style={{ 
            color: '#1d0c46',
            fontFamily: 'Raleway, sans-serif',
            fontWeight: 600
          }}>
            Delete Dashboard
          </span>
        }
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmLoading={deleting}
        okText="Delete"
        cancelText="Cancel"
        width={400}
        style={{ top: 20 }}
        styles={{
          content: {
            borderRadius: 0,
            padding: 0
          },
          header: {
            borderRadius: 0,
            borderBottom: '1px solid #E5E7EB',
            padding: '16px 24px',
            margin: 0
          },
          body: { 
            padding: '20px 24px',
            backgroundColor: '#ffffff',
            borderRadius: 0
          },
          footer: {
            borderRadius: 0,
            borderTop: '1px solid #E5E7EB',
            padding: '12px 24px'
          }
        }}
        okButtonProps={{
          className: "transition-all duration-200 hover:opacity-90",
          style: {
            backgroundColor: '#1d0c46',
            borderColor: '#1d0c46',
            border: 'none',
            color: '#ffffff',
            fontFamily: 'Raleway, sans-serif',
            fontWeight: 500,
            fontSize: '13px',
            borderRadius: '0px',
            padding: '6px 12px',
            height: '28px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px'
          }
        }}
        cancelButtonProps={{
          className: "transition-all duration-200 hover:opacity-90",
          style: {
            color: '#0f172a',
            borderColor: '#E5E7EB',
            borderWidth: '1px',
            borderStyle: 'solid',
            backgroundColor: '#ffffff',
            fontFamily: 'Raleway, sans-serif',
            fontWeight: 500,
            fontSize: '13px',
            borderRadius: '0px',
            padding: '6px 12px',
            height: '28px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px'
          }
        }}
      >
        <p style={{ 
          fontFamily: 'Raleway, sans-serif',
          fontSize: '16px',
          color: '#0f172a',
          margin: 0
        }}>
          Are you sure you want to delete the dashboard "{dashboardToDelete?.name}"? 
          This action cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

export default DashboardListContent;
